<script lang="ts">
import AppSelect from '@/components/v2/AppSelect.vue';

export default {
  name: 'AnalyticsUploadModal',
  components: {
    AppSelect,
  },
};

interface Document {
  name: string;
  type: string;
  accountId: number;
  file: File;
  selected: boolean;
  readyForUpload: boolean;
  tags?: string[];
}
</script>

<script setup lang="ts">
import { ref, reactive, watch, computed } from 'vue';
import AppModal from './v2/AppModal.vue';
import { AlertCircle, CloudUpload, Trash2, X } from '@lucide/vue';
import { SelectOption } from '@/types/components/Select';
import { accountService, tableauService } from '@/services/api';
import { documentService } from '@/services/v2/DocumentService';
import Combobox, { type ComboboxOption } from './v2/Combobox.vue';
import { DocumentType } from '@/types/v2/Document';
import { TableauType } from '@/types/TableauDashboard';
import { z, ZodError } from 'zod';
import { DocumentDomain } from '@/types/Document';
import { useRouter } from 'vue-router';
import { Category, getUploadTypesForCategory, UploadType } from '@/utilities/Analytics';
import { useStore } from 'vuex';

const router = useRouter();
const store = useStore();

// ------
// model
// ------
const open = defineModel<boolean>('open', { required: true });

// ------
// props
// ------
const props = defineProps<{
  analyticsTypeId: number;
  selectedCategoryName: string;
  accountId?: number; // if accountId is passed, then only that account will be selected
}>();

// ------
// emits
// ------
const emit = defineEmits<{
  submit: [];
}>();

// -----
// refs
// -----
const tableauBaseDomain = import.meta.env.VITE_TABLEAU_BASE_DOMAIN;
const selectedUploadType = ref<
  UploadType.TableauDashboard | UploadType.MarketingDocument | UploadType.ClientDocument
>();
const draggingOver = ref(false);
const fileInput = ref<HTMLInputElement | null>(null);
const documents = ref<Partial<Document>[]>([]);
const accounts = ref<SelectOption[]>([]);
const tableauTypes = ref<TableauType[]>([]);
const tableauTypeOptions = ref<SelectOption[]>([]);
const documentTypes = ref<DocumentType[]>([]);
const eligibleDocumentTypes = ref<SelectOption[]>([]);
const documentAccount = reactive({
  value: '',
});
const availableTags = ref<ComboboxOption[]>([]);
const selectedTags = ref<ComboboxOption[]>([]);
const selectedTag = ref<string>('');

const tableauFormFields = reactive({
  name: {
    value: '',
    error: '',
  },
  url: {
    value: '',
    error: '',
  },
  type: {
    value: '',
    error: '',
  },
  account: {
    value: '',
    error: '',
  },
});
const tableauSubmitError = ref<string>('');

// ---------
// computed
// ---------
const newVolatilityModelRoute = computed<string>(() => {
  if (props.accountId) {
    return `/account/${props.accountId}/analytics/category/${props.analyticsTypeId}/volatility/new`;
  }
  return `/analytics/category/${props.analyticsTypeId}/volatility/new`;
});

const newStockDropModelRoute = computed<string>(() => {
  if (props.accountId) {
    return `/account/${props.accountId}/analytics/category/${props.analyticsTypeId}/stockdrop/new`;
  }
  return `/analytics/category/${props.analyticsTypeId}/stockdrop/new`;
});

const newCyberModelRoute = computed<string>(() => {
  if (props.accountId) {
    return `/account/${props.accountId}/analytics/category/${props.analyticsTypeId}/cyber/new`;
  }
  return `/analytics/category/${props.analyticsTypeId}/cyber/new`;
});

const formSubmittable = computed(() => {
  return (
    (selectedUploadType.value === UploadType.TableauDashboard && validateTableauForm(false)) ||
    (selectedUploadType.value === UploadType.MarketingDocument &&
      documentsReadyForUpload.value &&
      documents.value.length > 0) ||
    (selectedUploadType.value === UploadType.ClientDocument &&
      documentsReadyForUpload.value &&
      documentAccount.value &&
      documents.value.length > 0)
  );
});

const documentsReadyForUpload = computed(() => {
  return documents.value.every((document) => document.readyForUpload);
});

const documentTypeDescription = computed(() => {
  return selectedUploadType.value === UploadType.MarketingDocument
    ? 'Upload non-client specific material, such as a D&O Market Update. Note that these documents will be available to all clients with access to the Platform when published.'
    : 'Upload a client-specific document, such as Catastrophe Modeling Results. These documents will all be available on the Analytics tab on a client page.';
});

const availableTagsFiltered = computed(() => {
  return availableTags.value.filter(tag => 
    !selectedTags.value.some(selectedTag => selectedTag.value === tag.value)
  );
});

// ---------
// watchers
// ---------
watch(open, async (value) => {
  // initialize stuff when modal first opens
  if (value) {
    selectedUploadType.value = undefined;

    // reset tableau form data
    tableauFormFields.name.value = '';
    tableauFormFields.name.error = '';
    tableauFormFields.url.value = '';
    tableauFormFields.url.error = '';
    tableauFormFields.type.value = '';
    tableauFormFields.type.error = '';
    tableauFormFields.account.value = '';
    tableauFormFields.account.error = '';
    tableauSubmitError.value = '';
    accounts.value.forEach((a) => (a.selected = false));

    // if accountId is passed, then only that account will be selected
    if (props.accountId) {
      accounts.value.forEach((a) => {
        a.selected = a.value === props.accountId!.toString();
      });
      documentAccount.value = props.accountId.toString();
    } else {
      documentAccount.value = '';
    }

    if (fileInput.value) {
      fileInput.value.value = '';
    }

    // reset document form data
    documents.value = [];
    
    // reset tags
    selectedTags.value = [];
    selectedTag.value = '';
  }
});

watch(selectedUploadType, async (value) => {
  tableauSubmitError.value = '';
  if (value === UploadType.TableauDashboard) {
    if (tableauTypes.value.length === 0) {
      tableauTypes.value = await tableauService.getTableauTypes();
    }

    tableauTypeOptions.value = tableauTypes.value
      .filter((t) => t.analyticsTypeId === props.analyticsTypeId)
      .map((t) => ({
        label: t.name,
        value: t.id.toString(),
      }));

    if (accounts.value.length === 0) {
      await populateAccounts();
    }
  }

  if (value === UploadType.MarketingDocument || value === UploadType.ClientDocument) {
    if (documentTypes.value.length === 0) {
      documentTypes.value = await documentService.getDocumentTypes();
    }

    eligibleDocumentTypes.value = documentTypes.value
      .filter((dt) => dt.analyticsTypeId === props.analyticsTypeId)
      .map((dt) => ({
        label: dt.name,
        value: dt.name,
      }));

    if (value === UploadType.ClientDocument && !props.accountId) {
      await populateAccounts();
    }

    // Load tags for document uploads
    if (availableTags.value.length === 0) {
      await getTags();
    }
  }

  // if accountId is passed, ensure account will be selected
  if (props.accountId) {
    accounts.value.forEach((a) => {
      a.selected = a.value === props.accountId!.toString();
    });
    documentAccount.value = props.accountId.toString();
  }
});

// --------
// methods
// --------
function handleFileDragOver(e: DragEvent) {
  e.preventDefault();
  e.stopPropagation();
  draggingOver.value = true;
}

function handleFileDragLeave() {
  draggingOver.value = false;
}

function handleFileDrop(e: DragEvent) {
  e.preventDefault();
  e.stopPropagation();
  draggingOver.value = false;

  if (e.dataTransfer?.items) {
    // use the DataTransferItemList interface if available
    let droppedDocuments: Partial<Document>[] = [];
    [...e.dataTransfer.items].forEach((item) => {
      if (item.kind === 'file') {
        const file = item.getAsFile();
        if (file) {
          if (file.type === '') {
            console.error('file type extension is required');
            return;
          }

          droppedDocuments.push({
            name: file.name,
            file: file,
            type: '',
          });
        }
      }
    });
    documents.value = [...documents.value, ...droppedDocuments];
  } else if (e.dataTransfer?.files) {
    // otherwise use the DataTransfer interface
    let droppedDocuments: Partial<Document>[] = [];
    [...e.dataTransfer.files].forEach((file) => {
      if (file.type === '') {
        console.error('file type extension is required');
        return;
      }

      droppedDocuments.push({
        name: file.name,
        file: file,
        type: '',
      });
    });
    documents.value = [...documents.value, ...droppedDocuments];
  }
}

function handleFilesSelected(e: Event) {
  const target = e.target as HTMLInputElement;
  if (target.files) {
    let selectedDocuments: Partial<Document>[] = [];
    [...target.files].forEach((file) => {
      if (file.type === '') {
        console.error('file type extension is required');
        return;
      }

      selectedDocuments.push({
        name: file.name,
        type: '',
        file: file,
      });
    });
    documents.value = [...documents.value, ...selectedDocuments];
  }

  e.preventDefault();
  e.stopPropagation();
}

function handleSelectFiles() {
  if (fileInput.value) {
    fileInput.value.click();
  }
}

function handleDeleteDocument(index: number) {
  documents.value.splice(index, 1);
}

function handleAccountSelect(account: SelectOption) {
  const foundAccount = accounts.value.find((a) => a.value === account.value);
  if (foundAccount) {
    foundAccount.selected = true;
  }
}

function handleVolatilitySelect() {
  router.push(newVolatilityModelRoute.value);
  open.value = false;
}

function handleStockDropSelect() {
  router.push(newStockDropModelRoute.value);
  open.value = false;
}

function handleCyberSelect() {
  router.push(newCyberModelRoute.value);
  open.value = false;
}

async function populateAccounts() {
  const accountList = await accountService.list();
  accounts.value = accountList.map((account) => {
    return {
      label: account.accountName,
      value: account.accountID.toString(),
    };
  });
}

async function getTags() {
  try {
    const tags = await documentService.getDocumentTags();
    availableTags.value = (tags || []).map(tag => ({
      label: tag.name,
      value: tag.name
    }));
  } catch (error) {
    console.error('Failed to load tags:', error);
    availableTags.value = [];
  }
}

function handleTagSelect(tag: ComboboxOption) {
  if (!selectedTags.value.some(selectedTag => selectedTag.value === tag.value)) {
    selectedTags.value.push(tag);
  }
  // Clear the selection after adding
  selectedTag.value = '';
}

function handleTagRemove(tag: ComboboxOption) {
  selectedTags.value = selectedTags.value.filter(selectedTag => selectedTag.value !== tag.value);
}

async function handleSubmit() {
  if (selectedUploadType.value === UploadType.TableauDashboard && validateTableauForm()) {
    tableauSubmitError.value = '';
    try {
      const parsed = Number(tableauFormFields.account.value);
      const accountId = Number.isNaN(parsed) || !tableauFormFields.account.value ? null : parsed;
      await tableauService.createDashboard(
        tableauFormFields.name.value,
        tableauFormFields.url.value,
        Number(tableauFormFields.type.value),
        accountId,
      );
      emit('submit');
      open.value = false;
    } catch (e) {
      console.error(e);
      tableauSubmitError.value = e instanceof Error ? e.message : 'Failed to create dashboard.';
    }
    return;
  } else if (selectedUploadType.value === UploadType.MarketingDocument && documentsReadyForUpload.value) {
    const requests: Promise<void>[] = [];
    const tags = selectedTags.value.map(tag => tag.value);

    for (const document of documents.value) {
      requests.push(
        documentService.uploadDocument({
          domain: DocumentDomain.ANALYTICS,
          type: document.type!,
          displayName: document.name,
          fileName: document.name!,
          document: document.file as File,
          analyticsTypeId: props.analyticsTypeId,
          tags: tags.length > 0 ? tags : undefined,
        }),
      );
    }

    try {
      await Promise.all(requests);
      emit('submit');
      open.value = false;
    } catch (e) {
      console.error('failed to upload documents.', e);
    }
    return;
  } else if (
    selectedUploadType.value === UploadType.ClientDocument &&
    documentsReadyForUpload.value &&
    documentAccount.value
  ) {
    const requests: Promise<void>[] = [];
    const tags = selectedTags.value.map(tag => tag.value);

    for (const document of documents.value) {
      requests.push(
        documentService.uploadDocumentForPolicyOrAccount({
          domain: DocumentDomain.ANALYTICS,
          type: document.type!,
          displayName: document.name,
          fileName: document.name!,
          document: document.file as File,
          accountId: Number(documentAccount.value),
          analyticsTypeId: props.analyticsTypeId,
          tags: tags.length > 0 ? tags : undefined,
        }),
      );
    }

    try {
      await Promise.all(requests);
      emit('submit');
      open.value = false;
    } catch (e) {
      console.error('failed to upload documents.', e);
    }
    return;
  }

  console.error('Form submission failed.');
}

function showUploadTypeOption(uploadType: UploadType): boolean {
  return getUploadTypesForCategory(store.state.user, props.selectedCategoryName as Category).includes(uploadType);
}

// --------------------
// methods: validators
// --------------------
function validateTableauForm(showErrors = true) {
  let isValid = true;

  // name
  try {
    z.string()
      .min(1, { message: 'Name is required' })
      .max(150, { message: 'Name must be less than 150 characters' })
      .parse(tableauFormFields.name.value);
    tableauFormFields.name.error = '';
  } catch (e) {
    if (e instanceof ZodError) {
      if (showErrors) {
        tableauFormFields.name.error = e.errors[0].message;
      }

      isValid = false;
    }
  }

  // url
  try {
    z.string()
      .min(1, { message: 'URL is required' })
      .url({ message: 'Invalid URL' })
      .refine((data) => data.startsWith('https://'), {
        message: "URL must start with 'https://'",
      })
      .refine(
        (data) => {
          try {
            const urlObj = new URL(data);
            return urlObj.hostname.includes(tableauBaseDomain);
          } catch (e) {
            return false;
          }
        },
        {
          message: `URL's domain must be ${tableauBaseDomain}`,
        },
      )
      .parse(tableauFormFields.url.value);
    tableauFormFields.url.error = '';
  } catch (e) {
    if (e instanceof ZodError) {
      if (showErrors) {
        tableauFormFields.url.error = e.errors[0].message;
      }

      isValid = false;
    }
  }

  // type
  const typeNumber = Number(tableauFormFields.type.value);
  if (!tableauFormFields.type.value || Number.isNaN(typeNumber) || typeNumber === 0) {
    if (showErrors) {
      tableauFormFields.type.error = 'Type is required';
    }
    isValid = false;
  } else {
    tableauFormFields.type.error = '';
  }

  // account (required when the account dropdown is shown)
  if (!props.accountId && props.selectedCategoryName !== Category.Advisory) {
    try {
      z.string()
        .min(1, { message: 'Account is required' })
        .parse(tableauFormFields.account.value);
      tableauFormFields.account.error = '';
    } catch (e) {
      if (e instanceof ZodError) {
        if (showErrors) {
          tableauFormFields.account.error = e.errors[0].message;
        }

        isValid = false;
      }
    }
  } else {
    tableauFormFields.account.error = '';
  }

  return isValid;
}
</script>

<template>
  <AppModal
    :show="open"
    :show-overflow="true"
    :handle-cancel="() => (open = false)"
    :handle-action="handleSubmit"
    :action-disabled="!formSubmittable">
    <template #content>
      <div v-if="!selectedUploadType" data-testId="upload" class="flex flex-col items-center gap-6">
        <h1 class="text-primary">Choose Upload Type</h1>
        <div class="flex gap-8">
          <button
            v-if="showUploadTypeOption(UploadType.TableauDashboard)"
            class="btn h-32 w-32 text-primary drop-shadow-sm hover:scale-105"
            @click="selectedUploadType = UploadType.TableauDashboard">
            Tableau Dashboard
          </button>
          <button
            v-if="showUploadTypeOption(UploadType.VolatilityModel)"
            data-testid="new-volatility-btn"
            class="btn h-32 w-32 text-primary drop-shadow-sm hover:scale-105"
            @click="handleVolatilitySelect">
            Volatility Model
          </button>
          <button
            v-if="showUploadTypeOption(UploadType.StockDropModel)"
            data-testid="new-stockdrop-btn"
            class="btn h-32 w-32 text-primary drop-shadow-sm hover:scale-105"
            @click="handleStockDropSelect">
            Stock Drop Model
          </button>
          <button
            v-if="showUploadTypeOption(UploadType.CyberModel)"
            data-testid="new-cyber-btn"
            class="btn h-32 w-32 text-primary drop-shadow-sm hover:scale-105"
            @click="handleCyberSelect">
            Cyber Model
          </button>
          <button
            v-if="Category.Advisory === selectedCategoryName && showUploadTypeOption(UploadType.Document)"
            class="btn h-32 w-32 text-primary drop-shadow-sm hover:scale-105"
            @click="selectedUploadType = UploadType.MarketingDocument">
            Advisory Resource
          </button>
          <button
            v-else-if="showUploadTypeOption(UploadType.Document)"
            class="btn h-32 w-32 text-primary drop-shadow-sm hover:scale-105"
            @click="selectedUploadType = UploadType.ClientDocument">
            Client Resource
          </button>
        </div>
      </div>
      <div v-if="selectedUploadType == UploadType.TableauDashboard" class="flex flex-col gap-2">
        <h1 class="text-primary">Upload Tableau Dashboard</h1>
        <!-- name -->
        <div>
          <label class="label">
            <span class="label-text text-xs text-gray-500">Name</span>
          </label>
          <input
            type="text"
            v-model="tableauFormFields.name.value"
            placeholder="Please enter a name"
            class="input input-sm w-full border border-solid" />
          <span class="text-xs text-red-500">{{ tableauFormFields.name.error }}</span>
        </div>
        <!-- url -->
        <div>
          <label class="label">
            <span class="label-text text-xs text-gray-500">URL</span>
          </label>
          <input
            type="text"
            v-model="tableauFormFields.url.value"
            placeholder="Please enter a valid tableau URL"
            class="input input-sm w-full border border-solid" />
          <span class="text-xs text-red-500">{{ tableauFormFields.url.error }}</span>
        </div>
        <!-- type-->
        <div>
          <label class="label w-1/3">
            <span class="label-text text-xs text-gray-500">Type</span>
          </label>
          <AppSelect
            v-model="tableauFormFields.type.value"
            :options="
              tableauTypeOptions.map((type) => ({
                label: type.label,
                value: Number(type.value),
              }))
            "
            class="w-full" />
          <span class="text-xs text-red-500">{{ tableauFormFields.type.error }}</span>
        </div>
        <!-- account -->
        <div class="w-full" v-if="!accountId && selectedCategoryName !== Category.Advisory">
          <div class="flex flex-col">
            <label class="label">
              <span class="label-text text-xs text-gray-500 pb-1">Account</span>
            </label>
            <Combobox
              input-class="input-sm"
              v-model="tableauFormFields.account.value"
              :options="accounts"
              :placeholder="'Select Account'" />
            <span class="text-xs text-red-500">{{ tableauFormFields.account.error }}</span>
          </div>
        </div>
        <div v-if="tableauSubmitError" role="alert" class="alert alert-error">
          <AlertCircle class="w-5 h-5 shrink-0" />
          <span>{{ tableauSubmitError }}</span>
        </div>
      </div>
      <!-- upload documents -->
      <div
        v-if="
          selectedUploadType && [UploadType.MarketingDocument, UploadType.ClientDocument].includes(selectedUploadType)
        "
        class="flex flex-col gap-6">
        <div class="flex flex-col">
          <h1 class="text-primary">
            {{ `Upload ${selectedUploadType === UploadType.MarketingDocument ? 'Advisory' : 'Client'} Resource` }}
          </h1>
          <p class="px-5 pt-2 text-xs italic">{{ documentTypeDescription }}</p>
        </div>
        <div
          class="p-4 py-0"
          @dragover="handleFileDragOver"
          @drop="handleFileDrop"
          @dragleave="handleFileDragLeave">
          <div
            class="rounded-lg border-2 border-dashed border-gray-400 p-2 transition-all"
            :class="{ 'bg-blue-100': draggingOver }">
            <div class="flex h-full flex-col items-center justify-center">
              <CloudUpload class="text-gray-400" />
              <div class="text-xs font-bold text-gray-400">Drag and drop files here</div>
              <div class="text-xs text-gray-400">or</div>
              <input ref="fileInput" type="file" multiple class="hidden" @change="handleFilesSelected" />
              <button class="btn btn-xs btn-secondary" @click.stop="handleSelectFiles">Select Files</button>
            </div>
          </div>
        </div>
        <!-- list of selected documents -->
        <div class="flex flex-col gap-2 p-4 py-2" ref="documentsSectionRef">
          <div v-for="(document, index) in documents" :key="index">
            <div
              class="flex cursor-pointer items-center justify-between gap-2 rounded-lg border border-gray-200 p-1 shadow-md transition-all">
              <div class="flex-1 overflow-hidden text-xs text-ellipsis text-gray-600 italic">
                {{ document.name }}
              </div>
              <AppSelect
                v-if="eligibleDocumentTypes.length > 0"
                v-model="document.type"
                :options="
                  eligibleDocumentTypes.map((type) => ({
                    label: type.label,
                    value: type.label,
                  })).sort((a, b) => a.label.localeCompare(b.label))
                "
                class="h-8 w-full"
                trigger-class="text-xs font-light"
                menu-class="text-xs font-light"
                @update:modelValue="document.readyForUpload = !!document.type"
                placeholder="Select Document Type" />
              <div
                class="group ml-5 flex h-6 w-6 items-center justify-center rounded-lg border border-solid border-gray-400 transition-all hover:border-red-300"
                @click="handleDeleteDocument(index)">
                <Trash2 :size="14" class="text-gray-400 transition-all group-hover:stroke-red-500" />
              </div>
            </div>
          </div>
        </div>
        <!-- list of accounts for client documents -->
        <div v-if="selectedUploadType === UploadType.ClientDocument && !accountId" class="px-4">
          <label class="label">
            <span class="label-text text-xs text-gray-500 pb-1">Client Account</span>
          </label>
          <div class="flex flex-col">
            <Combobox
              input-class="input-sm"
              v-model="documentAccount.value"
              :options="accounts"
              :placeholder="'Select Account'"
              @selected="handleAccountSelect" />
          </div>
        </div>
        <!-- Tags -->
        <div class="w-full px-4">
          <div class="flex flex-col">
            <label class="label">
              <span class="label-text text-xs text-gray-500 pb-1">Tags</span>
            </label>

            <div v-if="selectedTags.length > 0" class="flex flex-wrap pb-2">
              <div
                v-for="tag in selectedTags"
                :key="tag.value"
                @click="handleTagRemove(tag)"
                class="group badge flex h-fit cursor-pointer gap-1 badge-ghost badge-xs hover:bg-red-100">
                <div class="p-1">{{ tag.label }}</div>
                <X :size="12" class="group-hover:stroke-red-600" />
              </div>
            </div>

            <Combobox
              v-model="selectedTag"
              input-class="input-sm"
              :options="availableTagsFiltered"
              placeholder="Add tags..."
              :allow-free-text="true"
              @selected="handleTagSelect" />
          </div>
        </div>
      </div>
    </template>
  </AppModal>
</template>
